/**
 * @fileoverview "Edit image" on its own: turn and crop one picture, then Save or Cancel.
 *
 * For the pictures that have no editor of their own to put the tools in: a
 * photo waiting in Add photos, and a bed's cover. A photo already on a plant
 * or a bed is edited in its own editor (EditPhotoDialog), where the same
 * tools sit under the picture and its Save sends the edit with everything
 * else.
 *
 * The caller says how a save and a reset reach the server; this dialog only
 * collects the edit. "Reset to original" shows once the picture has been
 * edited before, and acts at once.
 *
 * Where: EditLocationDialog (a bed's cover) and PhotoTray (a photo waiting
 * in Add photos), as "Edit image".
 * API: none of its own; the caller's onSave and onReset reach the server
 * (PUT …/image and …/image/reset).
 * Settings: none.
 * Words (en/es): plants.card.editImage, plants.photo.edit.* (cropHint,
 * failed, resetFailed), sitewide.save, sitewide.cancel, sitewide.saving (Button loading).
 * Failures: one on our side (D31) says the standard sentence (errorMessage),
 * drawn with the support address as a link.
 *
 * @module ImageEditDialog
 */

import React from 'react';
import { Button } from '@/shared/components/ui/button';
import { ActionRow } from '@/shared/components/ui/ActionRow';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import type { ImageEdit } from '@/shared/lib/imageEdit';
import { PhotoDialogShell, PhotoPicture } from './PhotoDialogShell';
import { ImageEditStage } from './ImageEditStage';
import { ImageEditTools } from './ImageEditTools';
import { useImageEdit } from './useImageEdit';

/** The picture, and how its edit and reset are sent. */
interface ImageEditDialogProps {
  /** Whether the dialog is showing; each opening starts with no edit. */
  isOpen: boolean;
  /** Closes it (Cancel, or after a save). */
  onClose: () => void;
  /** The picture as stored now; nothing is drawn without one. */
  src: string | null;
  /** What it is of, for a screen reader. */
  alt: string;
  /** Whether the stored picture is already an edit, so it can be reset. */
  edited: boolean;
  /** Sends the edit. Rethrows on failure, so the dialog stays open and says so. */
  onSave: (edit: ImageEdit) => Promise<void>;
  /** Puts the original back. Rethrows on failure. */
  onReset: () => Promise<void>;
}

/**
 * Turn and crop one picture, then Save or Cancel.
 *
 * @param props - ImageEditDialogProps.
 */
export const ImageEditDialog: React.FC<ImageEditDialogProps> = ({
  isOpen, onClose, src, alt, edited, onSave, onReset,
}) => {
  const { reactTranslate: t } = useTranslation();
  const state = useImageEdit();
  const [saving, setSaving] = React.useState(false);
  const [resetting, setResetting] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const { clear } = state;
  // Each opening starts from the picture as it is.
  React.useEffect(() => {
    if (!isOpen) return;
    clear();
    setError(null);
    setSaving(false);
    setResetting(false);
  }, [isOpen, clear]);
  // A new picture is loading until it says otherwise.
  React.useEffect(() => { setLoading(true); }, [src]);

  /** Sends the edit and closes; a failure keeps the dialog open with the reason. */
  const save = async (): Promise<void> => {
    setError(null);
    setSaving(true);
    try {
      await onSave(state.edit);
      clear();
      onClose();
    } catch (err) {
      setError(errorMessage(err, t('plants.photo.edit.failed')));
    } finally {
      setSaving(false);
    }
  };

  /** Puts the original back, and leaves the dialog open on it. */
  const reset = async (): Promise<void> => {
    setError(null);
    setResetting(true);
    try {
      await onReset();
      clear();
    } catch (err) {
      setError(errorMessage(err, t('plants.photo.edit.resetFailed')));
    } finally {
      setResetting(false);
    }
  };

  const busy = saving || resetting;

  return (
    <PhotoDialogShell
      isOpen={isOpen}
      onClose={onClose}
      title={t('plants.card.editImage')}
      footer={(
        <ActionRow onCancel={onClose} disabled={busy}>
          <Button size="lg" onClick={() => void save()} loading={saving} disabled={busy || !state.changed}>
            {t('sitewide.save')}
          </Button>
        </ActionRow>
      )}
    >
      <PhotoPicture
        src={src}
        alt={alt}
        busy={busy || (loading && Boolean(src))}
        picture={src ? (
          <ImageEditStage src={src} alt={alt} state={state} onLoad={() => setLoading(false)} disabled={busy} />
        ) : undefined}
      />
      <ImageEditTools
        state={state}
        disabled={busy || !src}
        onReset={edited ? () => void reset() : undefined}
        resetting={resetting}
      />
      {state.edit.crop && (
        <p className="shrink-0 text-xs text-muted-foreground">{t('plants.photo.edit.cropHint')}</p>
      )}
      {error && <p className="shrink-0 text-sm text-destructive"><FailureText text={error} /></p>}
    </PhotoDialogShell>
  );
};
